import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Icon,
  Text
} from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import {
  MdArrowDownward,
  MdArrowUpward,
  MdUnfoldMore
} from 'react-icons/md';
import { SORT_DIRECTION } from '@constants';
import { isRowSelected } from './utils';
import {
  LoadingState,
  ErrorState,
  NoDataState
} from '@components/StateComponents';

/**
 * DataTable Component using TanStack Table
 *
 * A reusable table component for Libra Dashboard that displays data in rows
 * with clickable functionality to show details in sidebar.
 *
 * @param {Array} data - Array of data objects to display
 * @param {Array} columns - Column definitions (TanStack Table format)
 * @param {boolean} isLoading - Show loading spinner
 * @param {boolean} isError - Show error state
 * @param {string} errorMessage - Message to show when error occurs
 * @param {string} noDataFoundMessage - Message to show when no data
 * @param {function} onRowClick - Callback when row is clicked (receives row data)
 * @param {object} selectedRow - Currently selected row for highlighting
 * @param {string} rowKeyField - Field name to use as unique row identifier (default: 'id')
 * @param {object} columnFilters - Optional column filters for TanStack Table (default: undefined)
 * @param {function} onColumnFiltersChange - Optional callback for column filter changes (default: undefined)
 * @param {string} globalFilter - Optional global search string (default: undefined)
 * @param {function} onGlobalFilterChange - Optional callback for global filter changes (default: undefined)
 * @param {Array} sorting - Optional TanStack sorting state (default: undefined)
 * @param {function} onSortingChange - Optional callback for sorting changes (default: undefined)
 * @param {string} noMatchesMessage - Message shown when filters/search match 0 rows
 */
const DataTable = ({
  data = [],
  columns = [],
  isLoading = false,
  isError = false,
  errorMessage = 'An error occurred while loading data',
  noDataFoundMessage = 'No data available',
  onRowClick,
  selectedRow = null,
  rowKeyField = 'id',
  columnFilters,
  onColumnFiltersChange,
  globalFilter,
  onGlobalFilterChange,
  sorting,
  onSortingChange,
  noMatchesMessage = 'No matches for the current filters'
}) => {
  const tableConfig = {
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  };

  // Local config object passed into TanStack's `useReactTable({ state })`.
  // Named `tableState` (not `state`) to avoid confusion with React component
  // state — this is plain config, never re-rendered.
  const tableState = {};

  if (columnFilters !== undefined && onColumnFiltersChange !== undefined) {
    tableState.columnFilters = columnFilters;
    tableConfig.onColumnFiltersChange = onColumnFiltersChange;
    tableConfig.getFilteredRowModel = getFilteredRowModel();
  }

  if (globalFilter !== undefined && onGlobalFilterChange !== undefined) {
    tableState.globalFilter = globalFilter;
    tableConfig.onGlobalFilterChange = onGlobalFilterChange;
    tableConfig.getFilteredRowModel = getFilteredRowModel();
    tableConfig.globalFilterFn = 'includesString';
  }

  if (sorting !== undefined && onSortingChange !== undefined) {
    tableState.sorting = sorting;
    tableConfig.onSortingChange = onSortingChange;
    tableConfig.getSortedRowModel = getSortedRowModel();
  }

  if (Object.keys(tableState).length > 0) {
    tableConfig.state = tableState;
  }

  const table = useReactTable(tableConfig);

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState error={errorMessage} />;
  }

  if (!data || data.length === 0) {
    return <NoDataState message={noDataFoundMessage} />;
  }

  const visibleRows = table.getRowModel().rows;
  // TanStack's getHeaderGroups() always returns an array, but we guard with
  // `|| []` so the downstream `.map()` and `[0]` accesses can't throw if the
  // table instance is in a half-initialised state (e.g. during SSR or the
  // first render before columns are resolved).
  const headerGroups = table.getHeaderGroups() || [];
  const visibleColCount = headerGroups[0]?.headers.length || 1;

  const renderSortIcon = (header) => {
    if (!header.column.getCanSort()) return null;
    const sortDir = header.column.getIsSorted();
    if (sortDir === SORT_DIRECTION.ASC) {
      return <Icon as={MdArrowUpward} boxSize={3} color="whiteAlpha.900" />;
    }
    if (sortDir === SORT_DIRECTION.DESC) {
      return <Icon as={MdArrowDownward} boxSize={3} color="whiteAlpha.900" />;
    }
    return <Icon as={MdUnfoldMore} boxSize={3} color="whiteAlpha.500" />;
  };

  return (
    <TableContainer
      w="full"
      overflowX="auto"
      border="2px solid"
      borderColor="whiteAlpha.500"
      borderRadius="xl"
      bg="backGround"
    >
      <Table variant="unstyled" size="sm">
        <Thead position="sticky" top={0} zIndex={10} bg="#111111">
          {headerGroups.map((headerGroup) => (
            <Tr
              key={headerGroup.id}
              borderBottom="2px solid"
              borderColor="whiteAlpha.500"
            >
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                return (
                  <Th
                    key={header.id}
                    color="whiteAlpha.700"
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    py={4}
                    px={4}
                    whiteSpace="nowrap"
                    cursor={canSort ? 'pointer' : 'default'}
                    userSelect="none"
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    _hover={canSort ? { color: 'whiteAlpha.900' } : undefined}
                  >
                    <Flex align="center" gap={1}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {renderSortIcon(header)}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {visibleRows.length === 0 ? (
            <Tr>
              <Td
                colSpan={visibleColCount}
                py={6}
                px={4}
                textAlign="center"
                color="whiteAlpha.600"
                fontSize="sm"
              >
                <Text>{noMatchesMessage}</Text>
              </Td>
            </Tr>
          ) : (
            visibleRows.map((row) => {
              const isSelected = isRowSelected(
                row.original,
                selectedRow,
                rowKeyField
              );

              return (
                <Tr
                  key={row.id}
                  bg="backGround"
                  borderBottom="1px solid"
                  borderColor="whiteAlpha.200"
                  cursor={onRowClick ? 'pointer' : 'default'}
                  onClick={(e) => {
                    // Suppress the row-click when the user actually clicked
                    // an interactive child of the row. Two ways to opt out:
                    //   1. Native <button> (e.g. action buttons inside the
                    //      row) — covered by `closest('button')`.
                    //   2. Anything explicitly marked with the
                    //      `data-prevent-row-click` attribute (custom
                    //      dropdowns, popovers, checkboxes wrapped in divs,
                    //      etc.).
                    //
                    // `closest()` walks up the DOM from `e.target`, so this
                    // still works when the click lands on a nested icon /
                    // <span> inside a button rather than the button itself.
                    if (
                      e.target.closest('button') ||
                      e.target.closest('[data-prevent-row-click]')
                    ) {
                      return;
                    }
                    if (onRowClick) {
                      onRowClick(row.original);
                    }
                  }}
                  _hover={{
                    bg: isSelected ? 'altGray.600' : 'altGray.400'
                  }}
                  sx={
                    isSelected
                      ? {
                          bg: 'altGray.600'
                        }
                      : {}
                  }
                  transition="background-color 0.2s"
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      color="whiteAlpha.900"
                      fontSize="sm"
                      py={3}
                      px={4}
                      whiteSpace="nowrap"
                      maxW="300px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
